import * as React from "react";
import apiClient from "@/api-client";
import {
	Alert,
	AlertTitle,
	Autocomplete,
	Box,
	Button,
	Card,
	Chip,
	FormControl,
	Grid,
	MenuItem,
	Stack,
	Tab,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { CopySimpleIcon } from "@phosphor-icons/react/dist/ssr/CopySimple";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { dayjs } from "@/lib/dayjs";
import type { CampaignRequestMarketPayload, CampaignRequestMarketRefusal } from "@/hooks/api/use-campaign-requests";
import {
	campaignRequestErrorMessage,
	campaignRequestMarketRefusals,
	campaignRequestsKeys,
	useCampaignRequestMarketGuidance,
	useSaveCampaignRequestMarkets,
} from "@/hooks/api/use-campaign-requests";
import { CustomDatePicker } from "@/components/customDatePicker/custom-date-picker";
import type { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";

import {
	isoWeekdayOf,
	marketClock,
	marketFlag,
	marketLabel,
	marketTimezone,
	weekdayName,
	WEEKDAYS,
	weekdayShort,
} from "../constants";
import { sameNumbers, sameTime } from "./dirty";
import type { StepProps } from "./wizard-shell";
import { agentBadge, StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 3 — markets and schedule.
 *
 * Each tab here is literally a campaign. That is the whole architecture, surfaced as one panel per
 * market rather than as hidden objects — the client never hears the word, but provisioning creates
 * exactly one campaign per market on this step.
 *
 * A tab strip, and never a fold. The markets after the first used to sit behind a line of text that
 * had to be clicked to open: the lead market was on screen and the other three were a sentence, so
 * a schedule that was three-quarters unset looked finished. Every market is now one click away and
 * says on its tab whether it was refused.
 *
 * Times are local to each market and the zone is printed beside them, because 09:00 SGT is not
 * 09:00 AEST and a shared window that silently dials Australia at the wrong local hour is the most
 * common way a multi-market schedule goes wrong. "Copy to all markets" is not polish either: four
 * markets of hand-entered settings is data entry, and data entry is what drives people to email.
 */

const RETRY_INTERVALS = [1, 2, 4, 8, 12, 24, 48];
const MAX_ATTEMPTS = [1, 2, 3, 4, 5, 6];

interface MarketDraft {
	/** The persisted row, or null where this market has never been scheduled. */
	id: string | null;
	timezone: string;
	dialStartDate: string | null;
	dialEndDate: string | null;
	callingWindowStart: string;
	callingWindowEnd: string;
	callingDays: number[];
	originNumberId: number | null;
	/** For the picker only. The API stores the id. */
	originNumberLabel: string;
	redialIntervalHours: number | null;
	maxAttemptsPerPerson: number | null;
}

/** Postgres answers `HH:MM:SS`; an `<input type="time">` wants `HH:MM`. */
function toTimeInput(value: string | null | undefined): string {
	return value ? value.slice(0, 5) : "";
}

/**
 * What stops this card being written, in the client's words.
 *
 * The weekday rule is the one worth explaining. Sending no days at all used to be accepted, and
 * the API documented an empty list as "no day restriction" — which provisioning turns into
 * dialling seven days a week. So unticking every weekday, a gesture that plainly reads as "do not
 * call", quietly produced a campaign that called on Saturday and Sunday too, and nothing on this
 * screen ever said so. Refusing it is the safer half of that choice: the client is told, and told
 * before anything is written, rather than finding out from a call log.
 */
function problemsWith(draft: MarketDraft | undefined): string[] {
	if (!draft) return [];

	const problems: string[] = [];

	if (draft.callingDays.length === 0) {
		problems.push("Choose at least one day to call on. Leaving all of them off would dial every day of the week.");
	}
	if (draft.dialStartDate && draft.dialEndDate && draft.dialEndDate < draft.dialStartDate) {
		problems.push("The end date falls before the start date.");
	}

	return problems;
}

/** "Singapore, Malaysia and Thailand" — for a status line that names markets rather than counting them. */
function listMarkets(codes: string[]): string {
	const labels = codes.map((code) => marketLabel(code));
	if (labels.length <= 1) return labels[0] ?? "nothing";

	return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/**
 * An agent's name with the campaign taken off the front.
 *
 * Step 2 names an agent "<Campaign> — English", which is right on a quote line and wrong on a pill
 * inside the campaign it names: every tab on this screen belongs to the same campaign, so the
 * prefix is the one part of the name that cannot tell the client anything here. What is left is
 * handed to {@link agentBadge}, which drops it again where it is only the language repeated.
 */
function withoutCampaign(label: string | null | undefined, title: string): string {
	const named = (label ?? "").trim();
	const campaign = title.trim();

	if (campaign.length === 0 || !named.toLowerCase().startsWith(campaign.toLowerCase())) return named;

	// Only where the campaign is a whole word of the name and not the front of a longer one — a
	// campaign called "Poly" must not turn an agent called "Polyglot" into "glot". And where the
	// campaign *is* the whole name, the whole name stands: an empty pill says less than a repeated one.
	const rest = named.slice(campaign.length);
	if (!/^[\s—–-]/.test(rest)) return named;

	return rest.replace(/^[\s—–-]+/, "") || named;
}

/** "Monday and Friday" — the weekdays a flag is about, for a button that has to name them. */
function listDays(days: number[]): string {
	const names = days.map((day) => weekdayName(day));
	if (names.length <= 1) return names[0] ?? "";

	return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/** The settings that describe *when* to call, which are the ones worth copying between markets. */
type CopyableFields = Pick<
	MarketDraft,
	| "dialStartDate"
	| "dialEndDate"
	| "callingWindowStart"
	| "callingWindowEnd"
	| "callingDays"
	| "redialIntervalHours"
	| "maxAttemptsPerPerson"
>;

export function StepMarkets({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const markets = React.useMemo(() => request.markets ?? [], [request.markets]);

	const saveMutation = useSaveCampaignRequestMarkets();

	const [busy, setBusy] = React.useState(false);

	/** Which market is on screen. Every market's draft stays mounted in `drafts` regardless. */
	const [activeMarket, setActiveMarket] = React.useState<string>(() => markets[0] ?? "");

	// A market dropped on step 1 must not leave the strip pointing at a tab that no longer exists.
	React.useEffect(() => {
		if (markets.length > 0 && !markets.includes(activeMarket)) setActiveMarket(markets[0]);
	}, [activeMarket, markets]);

	/**
	 * What each market was refused for, whether by this screen or by the API.
	 *
	 * Per market rather than a single message, because the client is looking at four cards and
	 * "Could not save the schedule" beside four of them names none of them.
	 */
	const [refusals, setRefusals] = React.useState<CampaignRequestMarketRefusal[]>([]);
	/** Anything that refused the whole write without naming a market. */
	const [saveError, setSaveError] = React.useState<string | null>(null);

	/**
	 * The weekday flag (UX §06 note 4, canvas §05).
	 *
	 * Advisory and nothing else: it never disables Continue, it never gates a save, and dismissing
	 * it is a first-class answer — "keep Monday" is a decision, and the value is that we said it,
	 * on the record, before the campaign ran.
	 */
	const { data: guidance } = useCampaignRequestMarketGuidance(request.id);
	const [dismissed, setDismissed] = React.useState<string[]>([]);

	/**
	 * Whether this workspace has any number to dial from at all.
	 *
	 * The picker below is a search box: with nothing behind it, it opens onto the two words "No
	 * options" and stops there — which reads as "you have done something wrong" for a state that is
	 * both normal and ours to fix. Almost every first campaign is in it, because numbers are rented
	 * as part of the build. One row is enough to tell the two cases apart, so one row is asked for.
	 */
	const { data: numberPool } = useQuery({
		queryKey: [...campaignRequestsKeys.detail(request.id), "phone-numbers"],
		queryFn: async (): Promise<number> => {
			const response = await apiClient.get(`/campaign-requests/${request.id}/phone-numbers`, {
				params: { limit: 1, offset: 0 },
			});

			return Number(response.data?.total ?? response.data?.data?.length ?? 0);
		},
	});

	const hasNoNumbers = numberPool === 0;

	const [drafts, setDrafts] = React.useState<Record<string, MarketDraft>>(() => seed());

	function seed(): Record<string, MarketDraft> {
		const next: Record<string, MarketDraft> = {};

		for (const market of markets) {
			const saved = request.marketSchedules.find((schedule) => schedule.market === market);

			next[market] = {
				id: saved?.id ?? null,
				// The API has no default zone, and falling back to the browser's would dial three of
				// four markets at the wrong local hour without ever saying so.
				timezone: saved?.timezone ?? marketTimezone(market),
				dialStartDate: saved?.dialStartDate ?? null,
				dialEndDate: saved?.dialEndDate ?? null,
				callingWindowStart: toTimeInput(saved?.callingWindowStart) || "09:00",
				callingWindowEnd: toTimeInput(saved?.callingWindowEnd) || "18:00",
				callingDays: saved?.callingDays ?? [1, 2, 3, 4, 5],
				originNumberId: saved?.originNumberId ?? null,
				originNumberLabel: "",
				redialIntervalHours: saved?.redialIntervalHours ?? 4,
				maxAttemptsPerPerson: saved?.maxAttemptsPerPerson ?? 3,
			};
		}

		return next;
	}

	React.useEffect(() => {
		setDrafts(seed());
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `seed` closes over exactly these.
	}, [request.marketSchedules, markets]);

	const patch = (market: string, changes: Partial<MarketDraft>): void => {
		setDrafts((current) => ({ ...current, [market]: { ...current[market], ...changes } }));
	};

	/**
	 * Copies the schedule across, and only the schedule. The zone and the origin number stay where
	 * they are — those are what make a market a market, and copying them is the mistake this button
	 * exists to avoid.
	 */
	const copyToAll = (from: string): void => {
		setDrafts((current) => {
			const template = current[from];
			if (!template) return current;

			const copied: CopyableFields = {
				dialStartDate: template.dialStartDate,
				dialEndDate: template.dialEndDate,
				callingWindowStart: template.callingWindowStart,
				callingWindowEnd: template.callingWindowEnd,
				callingDays: [...template.callingDays],
				redialIntervalHours: template.redialIntervalHours,
				maxAttemptsPerPerson: template.maxAttemptsPerPerson,
			};

			const next: Record<string, MarketDraft> = { ...current };
			for (const market of markets) {
				if (market === from) continue;
				next[market] = { ...current[market], ...copied, callingDays: [...copied.callingDays] };
			}

			return next;
		});
	};

	/** What this market would be written as. Also what it is compared against to decide if it moved. */
	const payloadFor = React.useCallback(
		(market: string, draft: MarketDraft): CampaignRequestMarketPayload => ({
			market,
			timezone: draft.timezone,
			dialStartDate: draft.dialStartDate,
			dialEndDate: draft.dialEndDate,
			callingWindowStart: draft.callingWindowStart || null,
			callingWindowEnd: draft.callingWindowEnd || null,
			callingDays: draft.callingDays,
			originNumberId: draft.originNumberId,
			redialIntervalHours: draft.redialIntervalHours,
			maxAttemptsPerPerson: draft.maxAttemptsPerPerson,
			// Which agent answers here is decided on step 2 by the markets attached to it, so it
			// is derived rather than asked for twice.
			agentId: request.agents.find((agent) => agent.markets.includes(market))?.id ?? null,
		}),
		[request.agents]
	);

	/**
	 * The markets whose cards differ from what is stored.
	 *
	 * Unchanged rows are left out of the write entirely. A market write comes back through
	 * `recompute()` on the API, which withdraws a live quote and sends the request back for
	 * re-pricing, so walking through this step to re-read a schedule must not cost the client
	 * their quote. See `./dirty`.
	 */
	const changedMarkets = React.useMemo(() => {
		return markets.filter((market) => {
			const draft = drafts[market];
			if (!draft) return false;

			const payload = payloadFor(market, draft);
			const saved = draft.id ? request.marketSchedules.find((schedule) => schedule.id === draft.id) : undefined;

			return !(
				saved !== undefined &&
				saved.timezone === payload.timezone &&
				saved.dialStartDate === payload.dialStartDate &&
				saved.dialEndDate === payload.dialEndDate &&
				sameTime(draft.callingWindowStart, saved.callingWindowStart) &&
				sameTime(draft.callingWindowEnd, saved.callingWindowEnd) &&
				sameNumbers(saved.callingDays, draft.callingDays) &&
				saved.originNumberId === payload.originNumberId &&
				saved.redialIntervalHours === payload.redialIntervalHours &&
				saved.maxAttemptsPerPerson === payload.maxAttemptsPerPerson &&
				saved.agentId === payload.agentId
			);
		});
	}, [drafts, markets, payloadFor, request.marketSchedules]);

	// The rail is the one way off this step that writes nothing, so it has to ask first.
	useWizardDirty(changedMarkets.length > 0);

	/**
	 * Save the screen.
	 *
	 * One call for every card that moved, and the API writes them in a transaction. It used to be
	 * a sequential loop of one call per market: a 422 on the second — a dial end before its start
	 * — left the first market written, the third and fourth never attempted, and a toast naming
	 * only the second. The client was then holding a request with a schedule for one market out
	 * of four, with nothing on screen saying which.
	 *
	 * Now nothing is written unless all of it can be, and a refusal comes back per market so each
	 * complaint can be put on the card that caused it.
	 */
	const save = async (): Promise<boolean> => {
		setSaveError(null);

		// Checked here first so the client is answered without a round trip, and checked again by
		// the API because a screen is not a validator. Both refusals arrive in the same shape and
		// land on the same card.
		const local = markets
			.map((market) => ({ market, errors: problemsWith(drafts[market]) }))
			.filter(({ errors }) => errors.length > 0);

		setRefusals(local);
		// A refusal on a tab nobody is looking at is a refusal nobody can act on, so the strip moves
		// to the first market that was refused rather than waiting to be found.
		if (local.length > 0) {
			setActiveMarket(local[0].market);
			return false;
		}

		if (changedMarkets.length === 0) return true;

		setBusy(true);
		try {
			await saveMutation.mutateAsync({
				requestId: request.id,
				data: { markets: changedMarkets.map((market) => payloadFor(market, drafts[market])) },
			});

			// Per market, and it names the ones it left alone as well. "Schedule saved" over four
			// cards says nothing about which of the four, which is exactly what went wrong before.
			const untouched = markets.filter((market) => !changedMarkets.includes(market));
			toast.success(
				`Saved ${listMarkets(changedMarkets)}${untouched.length > 0 ? ` · ${listMarkets(untouched)} unchanged` : ""}`
			);
			return true;
		} catch (error: any) {
			const perMarket = campaignRequestMarketRefusals(error);

			setRefusals(perMarket);
			if (perMarket.length > 0) setActiveMarket(perMarket[0].market);
			// Only where the API had nothing per-market to say — a 403, a network failure, a body
			// the DTO refused before the handler ran. A banner beside cards already carrying their
			// own complaint is the noise this endpoint exists to remove.
			setSaveError(
				perMarket.length > 0 ? null : campaignRequestErrorMessage(error, "This schedule could not be saved.")
			);
			return false;
		} finally {
			setBusy(false);
		}
	};

	const handleContinue = async (): Promise<void> => {
		if (await save()) goTo(4);
	};

	const handleSaveExit = async (): Promise<void> => {
		if (await save()) exit();
	};

	/** Move the start to the next date that lands on one of the market's strong weekdays. */
	const shiftToStrongDay = (market: string, strongDays: number[]): void => {
		const draft = drafts[market];
		if (!draft || strongDays.length === 0) return;

		const from = draft.dialStartDate ? dayjs(draft.dialStartDate) : dayjs();
		const wanted = new Set(strongDays);

		// At most a week away by construction: some weekday in the next seven days is a strong one.
		for (let ahead = 1; ahead <= 7; ahead++) {
			const candidate = from.add(ahead, "day");
			if (wanted.has(isoWeekdayOf(candidate))) {
				patch(market, { dialStartDate: candidate.format("YYYY-MM-DD") });
				return;
			}
		}
	};

	/**
	 * Take the weak weekdays out of the calling days — the one-tap fix for the half of this flag
	 * that is about *which days* rather than which start date.
	 *
	 * Never down to nothing: an empty list provisions as every day of the week, so a fix that
	 * emptied the row would turn "stop calling on Monday" into "call on Sunday too". The caller
	 * only offers the button when something survives.
	 */
	const dropWeakDays = (market: string, weakDays: number[]): void => {
		const draft = drafts[market];
		if (!draft) return;

		const kept = draft.callingDays.filter((day) => !weakDays.includes(day));
		if (kept.length === 0) return;

		patch(market, { callingDays: kept });
	};

	const renderCard = (market: string, tabbed: boolean): React.JSX.Element | null => {
		const draft = drafts[market];
		if (!draft) return null;

		const agent = request.agents.find((candidate) => candidate.markets.includes(market));
		const clock = marketClock(market);
		/** Position zero: the market the client entered first, and the zone the programme reports in. */
		const isLeadMarket = markets[0] === market;

		/**
		 * The weekday flag, against both halves of the decision it is about.
		 *
		 * It used to test the dial start date alone, which meant it stayed silent through the whole
		 * of the choice it exists to inform: the client ticks Mon–Fri, and the one screen that knows
		 * Monday is this market's worst day says nothing until the start date happens to land on
		 * one. Calling *on* a weak day every week is the larger version of the same mistake, so the
		 * selected calling days raise it too.
		 *
		 * Still only where it is true. A flag against a Wednesday start with Tue–Thu ticked is
		 * noise, and noise is how a flag stops being read.
		 */
		const flag = guidance?.find((entry) => entry.market === market);
		const weakDays = flag?.weakDays ?? [];
		const strongDays = flag?.strongDays ?? [];
		const usable = weakDays.length > 0 && strongDays.length > 0;

		const startWeekday = draft.dialStartDate ? isoWeekdayOf(dayjs(draft.dialStartDate)) : null;
		const startsWeak = usable && startWeekday !== null && weakDays.includes(startWeekday);
		const weakChosen = usable ? weakDays.filter((day) => draft.callingDays.includes(day)) : [];

		const stillWeak = startsWeak || weakChosen.length > 0;
		const answered = dismissed.includes(market);
		/** The days this flag is actually about, which is what its buttons have to name. */
		const daysAtIssue = weakChosen.length > 0 ? weakChosen : startsWeak && startWeekday !== null ? [startWeekday] : [];
		/** Dropping every calling day would provision as *every* day, so it is only offered where something survives. */
		const canDropWeak = weakChosen.length > 0 && draft.callingDays.some((day) => !weakDays.includes(day));

		const refused = refusals.find((entry) => entry.market === market);

		return (
			<Card key={market} variant="outlined" sx={{ borderColor: refused ? "error.main" : "divider", p: 2 }}>
				<Stack spacing={2}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
						{/* The tab above already names the market. Without tabs there is nothing else saying it. */}
						{tabbed ? null : (
							<Typography variant="h6">
								{marketFlag(market)} {marketLabel(market)}
							</Typography>
						)}

						{/*
						 * "English", not "Poly Q3 — English · English". Step 2 names an agent after the
						 * campaign and the language it speaks; both halves are already on this screen —
						 * the campaign in the wizard's own heading, the language in the name — so what
						 * is left on the pill is whatever the client actually chose to call it.
						 */}
						{agent ? (
							<Chip
								color="primary"
								label={agentBadge(withoutCampaign(agent.label, request.title), agent.language)}
								size="small"
								variant="soft"
							/>
						) : (
							// A pill is a tag, not a sentence with a cross-reference in it.
							<Chip color="warning" label="No agent" size="small" variant="outlined" />
						)}
					</Stack>

					{/*
					 * On the market's own panel, not in a toast. The whole point of writing the markets
					 * as one thing is that a refusal can be put where the client can act on it — and
					 * when one is refused, none of the others were written either, which is what stops
					 * this screen from silently holding half a schedule.
					 */}
					{refused ? (
						<Alert severity="error">
							{/* The banner above already names the market and says nothing was written.
							    This is the part of it only this panel can answer. */}
							{refused.errors.map((error) => (
								<Typography key={error} variant="body2">
									{error}
								</Typography>
							))}
						</Alert>
					) : null}

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<CustomDatePicker
									label="Start"
									disabled={readOnly}
									value={draft.dialStartDate}
									minDate={dayjs()}
									setValue={(value) => patch(market, { dialStartDate: value })}
								/>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<CustomDatePicker
									label="End"
									disabled={readOnly}
									value={draft.dialEndDate}
									minDate={draft.dialStartDate ? dayjs(draft.dialStartDate) : dayjs()}
									setValue={(value) => patch(market, { dialEndDate: value })}
								/>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 6, md: 3 }}>
							<TextField
								fullWidth
								disabled={readOnly}
								type="time"
								label="Calling window from"
								value={draft.callingWindowStart}
								onChange={(event) => patch(market, { callingWindowStart: event.target.value })}
							/>
						</Grid>

						<Grid size={{ xs: 6, md: 3 }}>
							<TextField
								fullWidth
								disabled={readOnly}
								type="time"
								label="Calling window to"
								value={draft.callingWindowEnd}
								onChange={(event) => patch(market, { callingWindowEnd: event.target.value })}
								// The zone is printed against the window, always, and never inferred. The
								// heading has already said these times are local, so this says which zone
								// rather than saying "local" underneath it for a second time.
								helperText={clock}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Autocomplete
								disabled={readOnly}
								options={Intl.supportedValuesOf("timeZone")}
								popupIcon={<CaretDownIcon size={18} />}
								value={draft.timezone}
								onChange={(_event, value) => patch(market, { timezone: value ?? marketTimezone(market) })}
								renderInput={(params) => (
									<TextField
										{...params}
										required
										label="Timezone"
										/*
										 * Nothing to add on most markets: the window above already prints its
										 * zone and the heading already says times are local, so a third saying
										 * of one fact is all a helper would be.
										 *
										 * The first market is the exception, and it is the one fact that used
										 * to be a "Lead" pill on its tab — a word with no consequence attached
										 * to it. Provisioning rolls the whole programme's reporting day up in
										 * position zero's zone (`ProvisioningService`, `markets[0].timezone`),
										 * so the place to say so is against the zone it takes, not on a tab.
										 */
										helperText={
											isLeadMarket && markets.length > 1
												? "Reporting for the whole campaign follows this market's day — it is first on your list."
												: undefined
										}
									/>
								)}
							/>
						</Grid>

						<Grid size={12}>
							{/* No "Days" written over a row of buttons reading Mon Tue Wed. The days are the
							    control, and the name for it is only there for a reader who cannot see them. */}
							<ToggleButtonGroup
								size="small"
								aria-label="Days to call on"
								disabled={readOnly}
								value={draft.callingDays}
								onChange={(_event, value: number[]) => patch(market, { callingDays: value })}
							>
								{WEEKDAYS.map((weekday) => (
									<ToggleButton key={weekday.value} value={weekday.value}>
										{weekday.short}
									</ToggleButton>
								))}
							</ToggleButtonGroup>

							{/*
							 * Only when it is a warning. "Calls go out on Mon – Fri only" under a row of
							 * toggles already showing Mon–Fri lit is the screen reading itself back out.
							 * No days at all is not "no restriction" — it provisions as every day of the
							 * week — so that one is still said, in red, next to the toggles it is about.
							 */}
							{draft.callingDays.length === 0 ? (
								<Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
									Pick at least one day — no days would dial every day.
								</Typography>
							) : null}
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<FilterFetchSelect
									label="Origin number"
									showInputLabel
									disabled={readOnly}
									placeholder="Select a number to dial from"
									value={
										{
											label: draft.originNumberLabel,
											value: draft.originNumberId === null ? "" : String(draft.originNumberId),
										} as FilterOption
									}
									onChange={(selected: FilterOption | string) => {
										const raw = typeof selected === "string" ? selected : ((selected.value as string) ?? "");
										const label = typeof selected === "string" ? selected : ((selected.label as string) ?? "");
										const parsed = Number(raw);
										patch(market, {
											originNumberId: raw && Number.isFinite(parsed) ? parsed : null,
											originNumberLabel: label,
										});
									}}
									getOptionsUrl={`/campaign-requests/${request.id}/phone-numbers`}
									fetchLabel="number"
									fetchValue="id"
								/>

								{/*
								 * What the field is for, said under the field rather than in a hover — and
								 * when there is nothing to pick, what happens next instead of a dead end.
								 */}
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
									{hasNoNumbers
										? `Leave this blank — we rent a local ${marketLabel(market)} number and assign it before this market dials.`
										: `The number these calls come from. A local number is answered far more often, so we keep one per market.`}
								</Typography>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12, md: 3 }}>
							<TextField
								select
								fullWidth
								disabled={readOnly}
								label="If no answer, try again after"
								value={draft.redialIntervalHours ?? ""}
								onChange={(event) =>
									patch(market, { redialIntervalHours: event.target.value === "" ? null : Number(event.target.value) })
								}
							>
								{RETRY_INTERVALS.map((hours) => (
									<MenuItem key={hours} value={hours}>
										{hours} hours
									</MenuItem>
								))}
							</TextField>
						</Grid>

						<Grid size={{ xs: 12, md: 3 }}>
							<TextField
								select
								fullWidth
								disabled={readOnly}
								label="Maximum attempts per person"
								value={draft.maxAttemptsPerPerson ?? ""}
								onChange={(event) =>
									patch(market, {
										maxAttemptsPerPerson: event.target.value === "" ? null : Number(event.target.value),
									})
								}
							>
								{MAX_ATTEMPTS.map((attempts) => (
									<MenuItem key={attempts} value={attempts}>
										{attempts}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>

					{/* Never blocking, always cited, and every fix it offers is one tap. */}
					{stillWeak && flag && !answered && !readOnly ? (
						<Alert
							severity="info"
							action={
								<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
									{canDropWeak ? (
										<Button color="inherit" size="small" onClick={() => dropWeakDays(market, weakDays)}>
											Stop calling {listDays(weakChosen)}
										</Button>
									) : null}

									{startsWeak ? (
										<Button color="inherit" size="small" onClick={() => shiftToStrongDay(market, strongDays)}>
											Start {weekdayName(strongDays[0])} instead
										</Button>
									) : null}

									{/*
									 * The answer we are asking for, and a real one. Keeping a weak day is a
									 * decision the client is entitled to make; the value of the flag is that
									 * it was put to them before the campaign ran, not that they took it.
									 */}
									<Button color="inherit" size="small" onClick={() => setDismissed((current) => [...current, market])}>
										Keep {listDays(daysAtIssue)}
									</Button>
								</Stack>
							}
						>
							<AlertTitle>
								{weakDays.length === 1
									? `${weekdayName(weakDays[0])} is your weakest day in ${marketLabel(market)}`
									: `${listDays(weakDays)} are your weakest days in ${marketLabel(market)}`}
							</AlertTitle>
							<Typography variant="body2">
								{flag.basis === "observed"
									? // `additionalConnects` is this request's own volume moved off the weak days
										// entirely, so the sentence has to say that rather than name one fix: the
										// figure is the same whether they shift the start or drop the day.
										`Reach rate on ${weakDays.map((day) => weekdayShort(day)).join("/")} runs ${flag.deltaPercent}% below ${strongDays.map((day) => weekdayShort(day)).join("–")} when we call ${marketLabel(market)}.${flag.additionalConnects ? ` Keeping to ${listDays(strongDays)} would reach roughly ${flag.additionalConnects} more people at this volume.` : ""}`
									: `Reach rates run lower at the start and end of the week. We have not called ${marketLabel(market)} enough to put a number on it for you yet.`}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{flag.evidence}
							</Typography>
						</Alert>
					) : null}

					{/* The answer, kept on screen rather than dismissed into nothing — and reversible. */}
					{stillWeak && answered && !readOnly ? (
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
							<Typography variant="caption" color="text.secondary">
								You chose to keep {listDays(daysAtIssue)}.
							</Typography>
							<Button
								size="small"
								onClick={() => setDismissed((current) => current.filter((entry) => entry !== market))}
							>
								Show the note again
							</Button>
						</Stack>
					) : null}

					{/* Nothing to copy to where this is the only market. */}
					{readOnly || markets.length === 1 ? null : (
						<Box>
							<Button size="small" startIcon={<CopySimpleIcon />} onClick={() => copyToAll(market)}>
								Copy these settings to the other markets
							</Button>
							<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
								Dates, window, days and retry rules only.
							</Typography>
						</Box>
					)}
				</Stack>
			</Card>
		);
	};

	if (markets.length === 0) {
		return (
			<Stack spacing={3}>
				<StepHeading title="Set up each market and its schedule" />
				<Alert severity="warning">No markets chosen yet. Pick at least one on campaign basics.</Alert>
				<WizardFooter onBack={() => goTo(2)} onContinue={() => goTo(1)} continueLabel="Back to basics" />
			</Stack>
		);
	}

	return (
		<Stack spacing={3}>
			{/*
			 * Nothing on this step names a market — the markets were chosen on step 1 and this is
			 * where each one is set up. The heading says what is being done here, and the line under
			 * it says the one thing about it that catches people out.
			 */}
			<StepHeading
				title="Set up each market and its schedule"
				help={
					markets.length > 1
						? "Dates, hours and retry rules, market by market. Times are local to each one — set one up, then copy it across."
						: "Dates, hours and retry rules. Times are local to this market."
				}
			/>

			{/*
			 * The set, named. A refusal stops the whole write, so the client's first question is
			 * "what did that leave me with?" — and the answer, which is "exactly what you had", is
			 * worth saying before they go looking for it.
			 */}
			{refusals.length > 0 ? (
				<Alert severity="error">
					<AlertTitle>
						{refusals.length === 1
							? `${marketLabel(refusals[0].market)} needs fixing before this step can save`
							: `${listMarkets(refusals.map((entry) => entry.market))} need fixing before this step can save`}
					</AlertTitle>
					<Typography variant="body2">Nothing was written — your other markets are unchanged.</Typography>
				</Alert>
			) : null}

			{saveError ? <Alert severity="error">{saveError}</Alert> : null}

			{/*
			 * The four-line panel that used to stand here explained how number rental works, which is
			 * our architecture and not the client's decision. The one thing they have to do about it —
			 * leave the field blank — is said under the field itself, once per market.
			 *
			 * One tab per market. Two markets is the first point at which a strip earns its place; a
			 * strip of one is a heading with a line under it.
			 *
			 * The first market used to carry a "Lead" pill here. It is a word, not a setting: the
			 * only thing that follows from being first is the zone the programme reports in, and that
			 * is now said against the timezone field it is actually about.
			 */}
			{markets.length > 1 ? (
				<Box sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
					<Tabs value={activeMarket} variant="scrollable" onChange={(_event, value: string) => setActiveMarket(value)}>
						{markets.map((market) => (
							<Tab
								key={market}
								value={market}
								tabIndex={0}
								sx={{ minHeight: "auto" }}
								label={
									// A tab is a button, so everything in its label is phrasing content.
									<Stack component="span" direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
										<span>
											{marketFlag(market)} {marketLabel(market)}
										</span>

										{/* Which markets were refused, on the strip, so none of them has to be
										    hunted for one tab at a time. */}
										{refusals.some((entry) => entry.market === market) ? (
											<Box
												component="span"
												aria-label="Not saved"
												sx={{ bgcolor: "error.main", borderRadius: "50%", height: 8, width: 8 }}
											/>
										) : null}
									</Stack>
								}
							/>
						))}
					</Tabs>
				</Box>
			) : null}

			{renderCard(markets.length > 1 ? activeMarket : markets[0], markets.length > 1)}

			<WizardFooter
				busy={busy}
				onBack={() => goTo(2)}
				onSaveExit={readOnly ? undefined : () => void handleSaveExit()}
				onContinue={readOnly ? () => goTo(4) : () => void handleContinue()}
			/>
		</Stack>
	);
}
